








































































































import Vue from 'vue'
import axios, { AxiosResponse } from 'axios'
import query from 'querystring'
import { sprintf } from 'sprintf-js'
import { getText, getDefaultLanguage } from '../js/lang'
import { countHalfwidth } from '../js/string'
import LangSelector from '../components/lang-selector.vue'
import TicketPreview from '../components/ticket-preview.vue'
import TweetButton from '../components/tweet-button.vue'
import RetrieveView from '../components/retrieve-view.vue'
import CheckTicket from '../components/check-ticket.vue'
import TicketMenu from '../components/ticket-menu.vue'
import PublishedView from '../components/published-view.vue'
import ConfirmTicket from '../components/confirm-ticket.vue'
import TicketEditor from '../components/ticket-editor.vue'
import StampEditor from '../components/stamp-editor.vue'

import Countries from '../data/country'
const CountryMap = Object.fromEntries(Countries.map(c => [c.code, c]))

import ticketBackground from '../images/ticket-base.svg'

const NAME_CHAR_LIMIT = 32
const OUTGOING_CHAR_LIMIT = 64
const MISSION_CHAR_LIMIT = 256

interface Stamp {
  pos: [number, number]
  rot: number
}

interface Ticket {
  publish_id: string
  ticket_id: string
  name: string
  country_code?: string
  stamps: Stamp[]

  // database
  // id?: number
  // permit_publishing?: boolean
  confirm_publishing?: boolean
  mission_message?: string
  outgoing_message?: string
}

interface Validation {
  name?: string
  country_code?: string
  outgoing_message?: string
  mission_message?: string
  confirm_publishing?: boolean
}

interface Notification {
  success: string
  error: string
}

interface Query {
  lang: string
  publish_id: string
  view: string
}

interface ApiResponse {
  status: number
  result?: any
  ticket?: Ticket
  error?: {
    message: string
    validation?: Record<string, string>
  }
}

export default Vue.extend({
  name: 'ticket',
  components: {
    LangSelector,
    TicketPreview,
    TweetButton,
    RetrieveView,
    CheckTicket,
    TicketMenu,
    PublishedView,
    ConfirmTicket,
    TicketEditor,
    StampEditor
  },
  data() {
    return {
      // constants
      Countries,
      CountryMap,
      ticketBackground,
      NAME_CHAR_LIMIT,
      OUTGOING_CHAR_LIMIT,
      MISSION_CHAR_LIMIT,

      // environments
      backend: (process.env.HAYA2EXTICKET_BACKEND_API || '.') as string,
      debug: process.env.PARCEL_DEBUG ? true : false,

      // defaults
      lang: 'ja' as string,
      view: '' as string,

      name: '' as string,
      ticket_id: '' as string,
      country_code: 'JP' as string,
      outgoing_message: '' as string,
      mission_message: '' as string,
      confirm_publishing: false as boolean,

      notification: {
        success: '',
        error: '',
      } as Notification,

      query: {
        lang: 'ja',
        publish_id: '',
        view: '',
      } as Query,

      validation: {
        name: '',
        country_code: '',
        outgoing_message: '',
        mission_message: '',
        confirm_publishing: false,
      } as Validation,

      ticket: {
        publish_id: '',
        ticket_id: '',
        name: '',
        stamps: [],
      } as Ticket
    }
  },

  computed: {
    name_counter(): number {
      return countHalfwidth(this.name)
    },
    outgoing_message_counter(): number {
      return countHalfwidth(this.outgoing_message)
    },
    mission_message_counter(): number {
      return countHalfwidth(this.mission_message)
    },
    formatted_ticket_id(): string {
      if (!this.ticket.ticket_id) {
        return 'HAYA2-EX-****-****-**'
      }
      if (!this.ticket.country_code) {
        return this.ticket.ticket_id
      }
      return `${this.ticket.ticket_id}-${this.ticket.country_code}`
    },
    countries(): typeof Countries {
      if (this.lang === 'ja') {
        return Countries.sort((a, b) => (a.name.ja || '') < (b.name.ja || '') ? -1 : 1)
      }
      else {
        return Countries.sort((a, b) => (a.name.en || '') < (b.name.en || '') ? -1 : 1)
      }
    }
  },

  mounted() {
    if (document.location.search) {
      const match = document.location.search.match(/^\?(.*)/)
      if (match && match[1]) {
        const parsed = query.parse(match[1])
        this.query = {
          lang: (parsed.lang as string) || 'ja',
          publish_id: (parsed.publish_id as string) || '',
          view: (parsed.view as string) || ''
        }
      }
    }

    this.lang = this.query.lang || getDefaultLanguage()
    if (this.query.publish_id) {
      this.view = 'retrieve'
      this.retrieve()
    }
    else if (this.debug && this.query.view) {
      this.view = this.query.view
    }
  },

  methods: {
    l(key: string, ...values: any[]): string {
      return sprintf(getText(this.lang, key), ...values)
    },

    goToMenu(): void {
      this.view = 'menu' // Reset to menu
    },
    goToTicketEditor(): void {
      this.view = 'ticketEditor'
    },
    goToRetrieveForStamp(): void {
      this.view = 'retrieveForStamp'
    },

    async retrieveForStamp(): Promise<boolean> {
      try {
        const res: AxiosResponse<ApiResponse> = await axios.post(`${this.backend}/check.cgi`, {
          ticket_id: this.ticket_id,
        })

        if (res.data.status) {
          this.notification.error = res.data.error?.message || ''
          return false
        }

        this.ticket = res.data.ticket!
        this.outgoing_message = this.ticket.outgoing_message || ''
        this.mission_message = this.ticket.mission_message || ''
        this.confirm_publishing = this.ticket.confirm_publishing || false
        this.view = 'stampEditor'
        return true
      }
      catch (err: any) {
        console.error('err', err)
        this.notification.error = err.message
        return false
      }
    },

    async stamp(): Promise<boolean> {
      try {
        const res: AxiosResponse<ApiResponse> = await axios.post(`${this.backend}/stamp.cgi`, {
          ticket_id: this.ticket_id,
          outgoing_message: this.outgoing_message,
          mission_message: this.mission_message,
          confirm_publishing: this.confirm_publishing
        })

        if (res.data.status) {
          this.notification.error = res.data.error?.message || ''
          return false
        }

        this.ticket = res.data.ticket!
        this.view = 'published'
        return true
      }
      catch (err: any) {
        console.error('err', err)
        this.notification.error = err.message
        return false
      }
    },

    async confirmTicket(): Promise<boolean> {
      try {
        this.validation = {}

        let ret = true
        if (!this.name) {
          this.validation.name = this.l('error_name_not_found')
          ret = false
        }
        if (NAME_CHAR_LIMIT < this.name_counter) {
          this.validation.name = this.l('error_name_too_long')
          ret = false
        }
        if (OUTGOING_CHAR_LIMIT < this.outgoing_message_counter) {
          this.validation.outgoing_message = this.l('error_outgoing_message_too_long')
          ret = false
        }
        if (MISSION_CHAR_LIMIT < this.mission_message_counter) {
          this.validation.mission_message = this.l('error_mission_message_too_long')
          ret = false
        }

        if (!ret) {
          return ret
        }

        const res: AxiosResponse<ApiResponse> = await axios.post(`${this.backend}/confirm.cgi`, {
          name: this.name,
          country_code: this.country_code,
          outgoing_message: this.outgoing_message,
          mission_message: this.mission_message,
          confirm_publishing: this.confirm_publishing,
        })

        this.name = res.data.result.name
        this.country_code = res.data.result.country_code
        this.outgoing_message = res.data.result.outgoing_message
        this.mission_message = res.data.result.mission_message
        this.confirm_publishing = res.data.result.confirm_publishing

        if (res.data.status) {
          if (res.data.error?.validation) {
            const validation = { ...res.data.error.validation }
            for (const k of Object.keys(validation)) {
              validation[k] = this.l(validation[k])
            }
            this.validation = validation
          }
          else {
            this.notification.error = res.data.error?.message || ''
          }
          return false
        }

        this.view = 'confirmTicket'
        return true
      }
      catch (err: any) {
        console.error('err', err)
        this.notification.error = err.message
        return false
      }
    },

    async publish(): Promise<boolean> {
      try {
        if (!await this.confirmTicket()) {
          return false
        }

        const res: AxiosResponse<ApiResponse> = await axios.post(`${this.backend}/publish.cgi`, {
          name: this.name,
          country_code: this.country_code,
          outgoing_message: this.outgoing_message,
          mission_message: this.mission_message,
          confirm_publishing: this.confirm_publishing
        })

        if (res.data.status) {
          this.notification.error = res.data.error?.message || ''
          return false
        }

        this.ticket = res.data.ticket!
        this.view = 'published'
        return true
      }
      catch (err: any) {
        console.error('err', err)
        this.notification.error = err.message
        return false
      }
    },

    async checkTicket(): Promise<boolean> {
      try {
        const res: AxiosResponse<ApiResponse> = await axios.post(`${this.backend}/check.cgi`, {
          ticket_id: this.ticket_id,
        })

        if (res.data.status) {
          this.notification.error = res.data.error?.message || ''
          return false
        }

        this.ticket = res.data.ticket!
        this.view = 'published'
        return true
      }
      catch (err: any) {
        console.error('err', err)
        this.notification.error = err.message
        return false
      }
    },

    async retrieve(): Promise<boolean> {
      if (!this.query.publish_id) {
        this.notification.error = 'Invalid request.'
        return false
      }

      try {
        const res: AxiosResponse<ApiResponse> = await axios.post(`${this.backend}/retrieve.cgi`, {
          publish_id: this.query.publish_id
        })
        this.ticket = res.data.ticket!
        return true
      }
      catch (err: any) {
        console.error('err', err)
        this.notification.error = err.message
        return false
      }
    },

    download(): void {
      if (this.view !== 'published' || !this.ticket || !this.ticket.name || !this.ticket.ticket_id) {
        this.notification.error = 'Invalid request.'
        return
      }

      // Go to download.cgi

      const form = document.createElement('form')
      form.method = 'post'
      form.action = `${this.backend}/download.cgi`

      const ticketForm = document.createElement('input')
      ticketForm.type = 'hidden'
      ticketForm.name = 'ticket_id'
      ticketForm.value = this.ticket.ticket_id
      form.appendChild(ticketForm)

      const nameForm = document.createElement('input')
      nameForm.type = 'hidden'
      nameForm.name = 'name'
      nameForm.value = this.ticket.name
      form.appendChild(nameForm)

      if (this.debug) {
        window.open('', 'haya2ex-ticket-download')
        form.target = 'haya2ex-ticket-download'
      }

      document.body.appendChild(form)

      form.submit()
    },

    share(): void {
      if (!this.ticket || !this.ticket.publish_id) {
        this.notification.error = 'Invalid request.'
        return
      }
      window.open(`./ticket.html?publish_id=${this.ticket.publish_id}`)
    }
  }
})
